import { PayloadAction } from '@reduxjs/toolkit';
import {
  Fit,
  Layout,
  useRive,
  useViewModel,
  useViewModelInstance,
  useViewModelInstanceBoolean,
  useViewModelInstanceNumber,
  useViewModelInstanceString,
  useViewModelInstanceTrigger,
} from '@rive-app/react-webgl2';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { jettonActions, JettonStatus, selectWaitingEndsAtMs } from '@/entities/jetton';
import { selectUser } from '@/entities/user';
import { useCountdown } from '@/shared/lib/hooks/useCountdown';
import { useReduxAction } from '@/shared/lib/hooks/useReduxAction';

interface JettonPlayerProps {
  status: JettonStatus;
}

export function JettonPlayer({ status }: JettonPlayerProps) {
  const user = useSelector(selectUser);
  const waitingEndsAtMs = useSelector(selectWaitingEndsAtMs);
  const countdown = useCountdown(status === JettonStatus.COUNTDOWN ? waitingEndsAtMs : null);

  const { rive, RiveComponent } = useRive({
    src: '/assets/jetton/jetton.riv',
    artboard: 'playing',
    stateMachines: 'State Machine 1',
    autoplay: true,
    // Let Rive instantiate + bind the artboard's default view model itself,
    // instead of us creating an instance before the Context is ready.
    autoBind: true,
    layout: new Layout({ fit: Fit.Cover }),
  });

  // Resolve the artboard's default view model and its (auto-bound) instance.
  // Both stay null until `rive` is ready, so all property hooks below no-op
  // safely until binding completes.
  const viewModel = useViewModel(rive, { useDefault: true });
  const vmInstance = useViewModelInstance(viewModel, { rive, useDefault: true });

  const { trigger: triggerIdle } = useViewModelInstanceTrigger('trigger_idle', vmInstance);
  const { trigger: triggerStart } = useViewModelInstanceTrigger('trigger_start', vmInstance);
  const { trigger: triggerPreFlight } = useViewModelInstanceTrigger(
    'trigger_pre_flight',
    vmInstance,
  );
  const { trigger: triggerCrash } = useViewModelInstanceTrigger('trigger_crash', vmInstance);
  const { trigger: triggerCashout } = useViewModelInstanceTrigger('trigger_cashout', vmInstance);
  const { setValue: setCashout } = useViewModelInstanceNumber('cashout', vmInstance);

  const { setValue: setDynamicRun1 } = useViewModelInstanceString('dynamic_run1', vmInstance);
  const { setValue: setDynamicRun2 } = useViewModelInstanceString('dynamic_run2', vmInstance);
  const { setValue: setDynamicRun3 } = useViewModelInstanceString('dynamic_run3', vmInstance);
  const { setValue: setIsCountDown } = useViewModelInstanceBoolean('is_countdown', vmInstance);

  useEffect(() => {
    if (!vmInstance) return;
    setIsCountDown(status === JettonStatus.COUNTDOWN);
    if (status === JettonStatus.WAITING) {
      triggerIdle();
      setDynamicRun1('Waiting for next round...');
      setDynamicRun2('');
      setDynamicRun3('');
    }
    if (status === JettonStatus.COUNTDOWN) {
      if (countdown === 1) {
        triggerPreFlight();
      }
      setDynamicRun1('');
      setDynamicRun2('Starting in ');
      setDynamicRun3(`${countdown}s`);
    }
    if (status === JettonStatus.FLYING) {
      triggerPreFlight();
      triggerStart();
    }
  }, [status, countdown, vmInstance]);

  useReduxAction(jettonActions.wsCrash.type, () => {
    if (!vmInstance) return;
    triggerCrash();
  });

  useReduxAction<PayloadAction<{ userId: string; multiplier: number; payout: number }>>(
    jettonActions.wsCashoutEvent.type,
    (action) => {
      if (user?.id === action.payload.userId) {
        triggerCashout();
        setCashout(action.payload.payout);
      }
    },
  );

  return <RiveComponent style={{ width: '100%', height: '100%' }} />;
}
